import React, { memo } from 'react';
import styled, { css } from 'styled-components';

import colors from '@front/common/src/utils/colors';
import { toRem } from '@front/common/src/utils/styles';
import { CommonSwitchProps, SwitchVariant } from '@front/common/src/components/switch/types';
import { getBackgroundColor } from '@front/common/src/components/switch/utils';

import { getGradient } from '../../styles/gradient';

const checkedStyles = css`
  transform: translateX(${toRem(20)});
  transition:
    all 0.2s cubic-bezier(0.8, 0.4, 0.3, 1.25),
    background 0.15s ease;

  &::before {
    transform: scale(1);
    opacity: 0;
    transition: all 0.4s ease;
  }
`;

/* Hide checkbox visually but remain accessible to screen readers. */
/* Source: https://polished.js.org/docs/#hidevisually */
const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: ${toRem(1)};
  margin: ${toRem(-1)};
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: ${toRem(1)};
`;

const StyledSwitch = styled.div<{
  $isChecked: SwitchProps['isChecked'];
  $variant: SwitchVariant;
}>`
  position: relative;
  cursor: pointer;
  transform: translate3d(0, 0, 0);
  display: flex;

  &::before {
    content: '';
    position: relative;
    width: ${toRem(44)};
    height: ${toRem(24)};
    display: block;
    background: ${({ $isChecked, $variant }) =>
      getBackgroundColor({ isChecked: $isChecked, variant: $variant, gradient: getGradient() })};
    border-radius: ${toRem(100)};
    top: ${toRem(-1)};
    left: ${toRem(-1)};
    transition: background 0.2s ease;
  }
`;

const Toggler = styled.span<{
  $isChecked: boolean;
}>`
  position: absolute;
  transform: translate3d(0, 0, 0);
  top: 0;
  left: 0;
  width: ${toRem(22)};
  height: ${toRem(22)};
  display: block;
  background: ${colors.background};
  border-radius: ${toRem(11)};
  box-shadow: 0 ${toRem(1)} ${toRem(2)} 0 rgb(0 0 0 / 50%);
  transition: all 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    display: block;
    margin: ${toRem(-18)};
    width: ${toRem(56)};
    height: ${toRem(56)};
    border-radius: 50%;
  }
  ${({ $isChecked }) => $isChecked && checkedStyles};
`;

export type SwitchProps = CommonSwitchProps & {
  name: string;
  value?: string | number;
  id: string;
  className?: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
};

const Switch = ({ isChecked, name, value, onChange, id, className, variant = 'primary' }: SwitchProps) => (
  <StyledSwitch $isChecked={isChecked} $variant={variant} className={className}>
    <HiddenCheckbox checked={isChecked} name={name} value={value} onChange={onChange} id={id} />
    <Toggler $isChecked={isChecked} />
  </StyledSwitch>
);

export default memo(Switch);
